import {Module} from 'vuex';
import {RootState} from '@/store';
import PlanEditModel, {SolveModel, WarehouseDescription} from '@/models/PlanEditModel';
import {
    fetchApprove,
    fetchCopyData,
    fetchData,
    fetchDisapprove,
    fetchSolveCancel,
    fetchSolveProgress,
    fetchSolveRun,
    fetchWarehousePlywood,
    fetchWarehouseVeneer,
} from '@/api/journals/plan/edit';

type PlanEditState = {
    loading: boolean;
    planEdit: PlanEditModel | null;
    solve: SolveModel | null;
    solveProgressTimer: number | null;
    warehousePlywoodDescriptions: WarehouseDescription[] | null;
    warehouseVeneerDescriptions: WarehouseDescription[] | null;
    hasUnsavedChanges: boolean;
    planCalculationCount: number;
    disabledTabs: boolean;
};

const initialState = {
    planEdit: null,
    loading: false,
    solve: null,
    solveProgressTimer: null,
    warehousePlywoodDescriptions: null,
    warehouseVeneerDescriptions: null,
    hasUnsavedChanges: false,
    planCalculationCount: 0,
    disabledTabs: false,
};

const getInitialState = () => initialState;

const PlanEditModule: Module<PlanEditState, RootState> = {
    namespaced: true,
    state: {
        planEdit: null,
        loading: false,
        solve: null,
        solveProgressTimer: null,
        warehousePlywoodDescriptions: null,
        warehouseVeneerDescriptions: null,
        hasUnsavedChanges: false,
        planCalculationCount: 0,
        disabledTabs: false,
    },
    getters: {
        hasResult: state => !!state.planEdit?.IdLastPlanRes,
    },
    mutations: {
        setPlanEdit: (state, data) => {
            state.planEdit = data;
        },
        setLoading: (state, bool) => {
            state.loading = bool;
        },
        setSolve: (state, data) => {
            state.solve = data;
        },
        setSolveProgressTimer: (state, timer) => {
            state.solveProgressTimer = timer;
        },
        setWarehouseVeneer: (state, descriptions) => (state.warehouseVeneerDescriptions = descriptions),
        setWarehousePlywood: (state, descriptions) => (state.warehousePlywoodDescriptions = descriptions),
        setHasUnsavedChanges: (state, bool) => (state.hasUnsavedChanges = bool),
        clearState: state => Object.assign(state, getInitialState()),
        increasePlanCalculationCount: state => (state.planCalculationCount += 1),
        setDisabledTabs: (state, bool) => (state.disabledTabs = bool),
    },
    actions: {
        getExtendedView: ({commit, dispatch}, {externalData, id, needCopy}) => {
            return new Promise(async (resolve, reject) => {
                if (externalData) {
                    commit('setPlanEdit', JSON.parse(externalData));
                    resolve();
                }
                try {
                    let data;
                    if (needCopy) data = await dispatch('getCopy', id);
                    else {
                        const response = await fetchData(id);
                        data = response.data;
                    }
                    commit('setPlanEdit', data);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        getCopy: ({commit, state}, id: number) => {
            return new Promise(async (resolve, reject) => {
                commit('setLoading', true);
                try {
                    const {data} = await fetchCopyData(id);
                    resolve(data);
                } catch (e) {
                    reject(e);
                } finally {
                    commit('setLoading', false);
                }
            });
        },
        getSolveProgress: (
            {commit, state, dispatch},
            {id, needUpdateExtendedView}: {id: number; needUpdateExtendedView: boolean | undefined},
        ) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchSolveProgress(id);
                    commit('setSolve', data);
                    if (!data.IsFinalState) {
                        commit('setPlanEdit', Object.assign(state.planEdit, {State: data.Message}));
                        commit(
                            'setSolveProgressTimer',
                            setTimeout(() => dispatch('getSolveProgress', {id, needUpdateExtendedView: true}), 500),
                        );
                    } else if (needUpdateExtendedView) {
                        await dispatch('getExtendedView', {id});
                        if (state.planEdit?.IdLastPlanRes) {
                            await dispatch('planEditResult/getResult', state.planEdit.IdLastPlanRes, {root: true});
                            commit('increasePlanCalculationCount');
                        }
                    }
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        solveRun: async ({commit, dispatch}, id: number) => {
            try {
                const {data} = await fetchSolveRun(id);
                commit('setSolve', data);
                dispatch('getSolveProgress', {id});
            } catch (e) {
                console.error(e);
            }
        },
        solveCancel: async ({commit}, id: number) => {
            try {
                const {data} = await fetchSolveCancel(id);
                commit('setSolve', data);
            } catch (e) {
                console.error(e);
            }
        },
        init: async ({commit, dispatch, state}, payload: {externalData: string; id: number; needCopy: boolean}) => {
            commit('setLoading', true);
            try {
                dispatch('getWarehouseVeneerDescriptions');
                dispatch('getWarehousePlywoodDescriptions');
                if (payload.needCopy) {
                    await dispatch('getExtendedView', payload);
                    await dispatch('getSolveProgress', {id: state.planEdit?.Id});
                } else {
                    await Promise.all([
                        dispatch('getExtendedView', payload),
                        dispatch('getSolveProgress', {id: payload.id}),
                    ]);
                }
            } finally {
                commit('setLoading', false);
            }
        },
        clearSolveProgressTimer: ({commit, state}) => {
            if (state.solveProgressTimer) {
                clearTimeout(state.solveProgressTimer);
                commit('setSolveProgressTimer', null);
            }
        },
        getWarehouseVeneerDescriptions: ({commit}) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchWarehouseVeneer();
                    commit('setWarehouseVeneer', data);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        getWarehousePlywoodDescriptions: ({commit}) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchWarehousePlywood();
                    commit('setWarehousePlywood', data);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        approvePlan: ({commit}, id: number) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchApprove(id);
                    commit('setPlanEdit', data);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        disapprovePlan: ({commit}, id: number) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchDisapprove(id);
                    commit('setPlanEdit', data);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
    },
};

export default PlanEditModule;
